import type { ReactNode } from 'react';

/**
 * Peças visuais compartilhadas pelas telas de operação.
 *
 * Existem para que oito telas diferentes digam a mesma coisa do mesmo jeito:
 * um número em destaque, um estado, uma tabela vazia. Sem elas, cada tela
 * inventaria a sua própria régua de severidade — e "atrasado" deixaria de ter
 * uma cor só no sistema inteiro.
 */

export type Tom = 'neutro' | 'sucesso' | 'alerta' | 'erro' | 'informacao' | 'dourado';

const CLASSES_DE_PILULA: Record<Tom, string> = {
  neutro: 'bg-marinho-suave text-marinho',
  sucesso: 'bg-sucesso/10 text-sucesso',
  alerta: 'bg-alerta/10 text-alerta',
  erro: 'bg-erro/10 text-erro',
  informacao: 'bg-informacao/10 text-informacao',
  dourado: 'bg-dourado-suave text-alerta',
};

export function Pilula({
  children,
  tom = 'neutro',
}: {
  children: ReactNode;
  tom?: Tom;
}): React.JSX.Element {
  return (
    <span
      className={`inline-block rounded-full px-2 py-1.25 text-[10px] font-[850] ${CLASSES_DE_PILULA[tom]}`}
    >
      {children}
    </span>
  );
}

/**
 * Cabeçalho de página no formato do protótipo: uma sobrancelha que diz de que
 * área da operação a tela trata, o nome da tela, uma linha do que ela resolve,
 * e as ações à direita.
 */
export function CabecalhoDePagina({
  area,
  titulo,
  descricao,
  acoes,
}: {
  area: string;
  titulo: string;
  descricao?: string;
  acoes?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="text-[11px] font-[850] uppercase tracking-[0.12em] text-marinho-claro">
          {area}
        </div>
        <h1 className="my-1 text-[28px] font-bold leading-tight text-marinho">{titulo}</h1>
        {descricao ? <div className="text-[13px] text-texto-suave">{descricao}</div> : null}
      </div>
      {acoes ? <div className="flex flex-wrap items-center gap-2">{acoes}</div> : null}
    </div>
  );
}

/**
 * Faixa de aviso: uma regra do sistema que a tela precisa declarar antes que
 * alguém use, como a que diz que o pagamento do 1º boleto define a vigência.
 * Não é erro nem alerta — é o funcionamento, dito em voz baixa.
 */
/**
 * Os três tons que o protótipo define para a faixa: `.notice`, `.notice
 * .success` e `.notice .warning`. Os demais tons do sistema não têm faixa
 * desenhada, e por isso ficam de fora do tipo em vez de ganhar cor inventada.
 */
type TomDeAviso = Extract<Tom, 'neutro' | 'sucesso' | 'alerta'>;

const CLASSES_DE_AVISO: Record<TomDeAviso, string> = {
  neutro: 'border-[#dcecea] bg-[#f6faf9]',
  sucesso: 'border-[#cdebd9] bg-[#ecf8f1]',
  alerta: 'border-[#f2dfb8] bg-[#fff8eb]',
};

export function Aviso({
  children,
  tom = 'neutro',
}: {
  children: ReactNode;
  tom?: TomDeAviso;
}): React.JSX.Element {
  const aparencia = `mb-3 rounded-[11px] border px-3.25 py-3 text-xs text-[#395b62]`;

  return <div className={`${aparencia} ${CLASSES_DE_AVISO[tom]}`}>{children}</div>;
}

const CLASSES_DE_MEDALHA: Record<string, string> = {
  BRONZE: 'bg-bronze-fundo text-bronze',
  PRATA: 'bg-prata-fundo text-prata',
  OURO: 'bg-ouro-fundo text-ouro',
  DIAMANTE: 'bg-diamante-fundo text-diamante',
};

/**
 * Medalha de score.
 *
 * Diferente da Pílula: cada score tem a sua cor fixa, e é por ela que se
 * reconhece um Diamante numa lista de cinquenta linhas sem ler o texto.
 */
export function Medalha({
  score,
  children,
}: {
  score: string;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.25 rounded-full border border-black/10 px-2.25 py-1.25 text-[10px] font-black whitespace-nowrap ${
        CLASSES_DE_MEDALHA[score] ?? 'bg-marinho-suave text-marinho'
      }`}
    >
      {children}
    </span>
  );
}

export function Cartao({
  titulo,
  descricao,
  acao,
  children,
  className = '',
}: {
  titulo?: string;
  descricao?: string;
  acao?: ReactNode;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    // `min-w-0` porque item de grade nasce com `min-width: auto` e se recusa a
    // encolher abaixo do próprio min-content: uma linha com `truncate` fixava
    // esse piso em 398px, e num celular de 390px a coluna inteira ia junto —
    // arrastando os Cartões vizinhos, que cabiam folgados.
    <section
      className={`min-w-0 rounded-2xl border border-linha bg-superficie p-4.25 shadow-card ${className}`}
    >
      {titulo ? (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-[850] text-marinho">{titulo}</h2>
            {descricao ? <p className="text-[12px] text-texto-suave">{descricao}</p> : null}
          </div>
          {acao}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Natureza do indicador, fixa: diz do que ele trata, não como ele está hoje. */
export type Severidade = 'erro' | 'alerta' | 'sucesso';

export function Indicador({
  teste,
  rotulo,
  valor,
  detalhe,
  tom = 'neutro',
  severidade,
}: {
  teste?: string;
  rotulo: string;
  valor: string;
  detalhe?: string;
  tom?: Tom;
  severidade?: Severidade;
}): React.JSX.Element {
  const corDoValor =
    tom === 'erro'
      ? 'text-erro'
      : tom === 'alerta'
        ? 'text-alerta'
        : tom === 'sucesso'
          ? 'text-sucesso'
          : 'text-marinho';

  // A faixa na borda esquerda marca do que o card trata e não muda com o
  // valor — é assim no protótipo, e é o que deixa os quatro alertas
  // reconhecíveis de relance mesmo quando estão todos zerados.
  const faixaDeSeveridade =
    severidade === 'erro'
      ? 'border-l-4 border-l-erro'
      : severidade === 'alerta'
        ? 'border-l-4 border-l-dourado'
        : severidade === 'sucesso'
          ? 'border-l-4 border-l-sucesso'
          : '';

  return (
    <div
      data-teste={teste}
      className={`rounded-2xl border border-linha bg-superficie p-4.25 shadow-card ${faixaDeSeveridade}`}
    >
      <p className="text-[10px] font-[850] uppercase tracking-wider text-texto-suave">
        {rotulo}
      </p>
      {/* O corpo do número acompanha a largura da coluna e nunca quebra: um
       * valor partido no meio ("R$ 150.00 / 0,00") se lê como dois números.
       *
       * O protótipo dá dois tamanhos ao número: menor na faixa de alerta, que
       * conta uma pendência, e maior na linha de métricas, que é o placar. */}
      <p
        className={`mt-1 leading-tight whitespace-nowrap ${
          severidade ? 'text-[20px] font-bold' : 'text-[clamp(1rem,1.67vw,1.5rem)] font-black'
        } ${corDoValor}`}
      >
        {valor}
      </p>
      {detalhe ? <p className="mt-1.25 text-[11px] text-texto-suave">{detalhe}</p> : null}
    </div>
  );
}

/**
 * Tabela com rolagem horizontal própria.
 *
 * O contêiner rola em vez da página inteira: numa tela de 12 colunas, deixar a
 * página rolar de lado tira o menu e o cabeçalho do campo de visão.
 */
export function Tabela({
  colunas,
  children,
  vazio,
  carregando = false,
  teste,
}: {
  colunas: string[];
  children: ReactNode;
  vazio: string;
  carregando?: boolean;
  teste?: string;
}): React.JSX.Element {
  const semLinhas = Array.isArray(children) ? children.flat().length === 0 : !children;

  return (
    <div
      data-teste={teste}
      className="overflow-x-auto rounded-2xl border border-linha bg-superficie shadow-card"
    >
      <table className={CLASSE_TABELA}>
        <CabecalhoDeTabela colunas={colunas} />
        <tbody>
          {semLinhas ? (
            <tr>
              <td colSpan={colunas.length} className="px-3.25 py-8 text-center text-texto-suave">
                {carregando ? 'Carregando…' : vazio}
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * O cabeçalho de tabela do protótipo: caixa-alta miúda sobre faixa fria.
 *
 * Vive aqui porque cinco telas montavam o próprio `<thead>` à mão, cada uma
 * com a sua medida — e o resultado ficava 40% maior que o do protótipo, largo
 * a ponto de empurrar a coluna de ação para fora da tela.
 */
export function CabecalhoDeTabela({ colunas }: { colunas: string[] }): React.JSX.Element {
  return (
    <thead className="border-b border-linha bg-[#f8fafb] text-texto-suave">
      <tr>
        {colunas.map((coluna) => (
          <th
            key={coluna}
            className={`${CLASSE_CELULA} text-[10px] font-bold uppercase tracking-[0.04em]`}
          >
            {coluna}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** Medidas do protótipo: 12px de corpo, 13px de respiro lateral. */
const CLASSE_CELULA = 'px-3.25 py-3';
export const CLASSE_TABELA = 'w-full text-left text-xs';

export function Celula({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return <td className={`${CLASSE_CELULA} ${className}`}>{children}</td>;
}

export function Linha({ children }: { children: ReactNode }): React.JSX.Element {
  return <tr className="border-b border-linha last:border-0">{children}</tr>;
}

/**
 * Os dois tamanhos do protótipo: `.btn` e `.btn.action-mini`.
 *
 * O miúdo existe porque botão de tamanho normal dentro de linha de tabela
 * empurra as últimas colunas para fora da tela — a de ação inclusive, que é
 * justamente a que a pessoa foi clicar.
 */
const TAMANHOS_DE_BOTAO = {
  normal: 'rounded-[10px] px-3.25 py-2.5 text-base',
  mini: 'rounded-[8px] px-2 py-1.5 text-[10px]',
};

export function Botao({
  children,
  onClick,
  tipo = 'button',
  variante = 'secundario',
  tamanho = 'normal',
  desabilitado = false,
  teste,
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'button' | 'submit';
  variante?: 'primario' | 'secundario';
  tamanho?: keyof typeof TAMANHOS_DE_BOTAO;
  desabilitado?: boolean;
  teste?: string;
}): React.JSX.Element {
  const classes =
    variante === 'primario'
      ? 'bg-marinho text-white hover:bg-marinho-claro'
      : 'border border-linha bg-superficie hover:bg-marinho-suave';

  return (
    <button
      type={tipo === 'submit' ? 'submit' : 'button'}
      data-teste={teste}
      onClick={onClick}
      disabled={desabilitado}
      className={`${TAMANHOS_DE_BOTAO[tamanho]} font-[750] whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-50 ${classes}`}
    >
      {children}
    </button>
  );
}

/** Linha de lista com rótulo à esquerda e valor à direita. */
export function LinhaDeLista({
  titulo,
  detalhe,
  valor,
  complemento,
  teste,
}: {
  titulo: ReactNode;
  detalhe?: ReactNode;
  valor?: ReactNode;
  complemento?: ReactNode;
  teste?: string;
}): React.JSX.Element {
  return (
    <div
      data-teste={teste}
      className="flex items-center justify-between gap-3 border-b border-linha py-3 last:border-0"
    >
      <div className="min-w-0">
        <p className="truncate font-medium text-marinho">{titulo}</p>
        {detalhe ? <p className="truncate text-xs text-texto-suave">{detalhe}</p> : null}
      </div>
      {valor !== undefined ? (
        <div className="shrink-0 text-right">
          <p className="font-semibold text-marinho">{valor}</p>
          {complemento ? <p className="text-xs text-texto-suave">{complemento}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Botão de "Anterior" e "Próxima" de um rodapé de páginas.
 *
 * Mora aqui desde que a trilha de Auditoria passou a paginar: o botão era da
 * parede de Anúncios, e copiá-lo teria deixado duas aparências para a mesma
 * peça, divergindo na primeira vez que alguém mexesse numa só.
 */
export function BotaoDePagina({
  rotulo,
  desabilitado,
  aoClicar,
}: {
  rotulo: string;
  desabilitado: boolean;
  aoClicar: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={aoClicar}
      disabled={desabilitado}
      className="min-h-11 rounded-full border border-linha px-4 text-xs font-[850] text-marinho transition disabled:cursor-not-allowed disabled:text-texto-suave disabled:opacity-50"
    >
      {rotulo}
    </button>
  );
}

export function data(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}
